import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { EXCHANGE_NAMES, exchangeLabel, isKnownExchange, type KnownExchange } from "@/lib/labels";
import asterLogo from "./exchange-logos/aster.png";
import binanceLogo from "./exchange-logos/binance.png";
import bingxLogo from "./exchange-logos/bingx.png";
import bitgetLogo from "./exchange-logos/bitget.png";
import bybitLogo from "./exchange-logos/bybit.png";
import coinbaseLogo from "./exchange-logos/coinbase.png";
import gateLogo from "./exchange-logos/gate.png";
import htxLogo from "./exchange-logos/htx.png";
import hyperliquidLogo from "./exchange-logos/hyperliquid.png";
import krakenLogo from "./exchange-logos/kraken.png";
import kucoinLogo from "./exchange-logos/kucoin.png";
import lighterLogo from "./exchange-logos/lighter.png";
import mexcLogo from "./exchange-logos/mexc.png";
import okxLogo from "./exchange-logos/okx.png";

/**
 * Знак биржи §9 (1.74, §87) — знак-приложение самой биржи, как её favicon
 * или иконка приложения: 1:1, фирменный фон и цвета, одинаковые в обеих
 * темах. Поэтому знак — файл для `<img>`, а не встроенный SVG с
 * `currentColor` и классами `dark:` (решение 6 design `stage8-exchange-labels`
 * пересмотрено). Официального SVG знака-приложения нет ни у одной из
 * бирж, поэтому файлы — PNG, уменьшенные до 64 px без перерисовки.
 *
 * Идентификаторы бирж — те же, что приходят в `instrument.exchange`; знак
 * есть у каждой биржи, адаптер которой есть у демона. Имя биржи словом и
 * проверка известности — общие `EXCHANGE_NAMES`/`isKnownExchange` из
 * `@/lib/labels` (`exchangeLabel`): подпись рядом со знаком и подпись всюду
 * в интерфейсе обязаны совпадать буква в букву.
 */

/**
 * Высота знака по умолчанию; ширина равна высоте. Углы скруглены, как у знака
 * в макете Claude Design (просьба владельца 14.10.2026): радиус в долях
 * стороны, чтобы знак любого размера скруглялся одинаково.
 */
const DEFAULT_LOGO_CLASS = "aspect-square h-4 w-auto rounded-[20%]";

/**
 * Источники знаков, все загружены 14.10.2026:
 *
 * - `binance` — иконка приложения в App Store (id 1436799971):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple211/v4/2e/de/33/2ede3395-134e-78f9-a307-230dea6e7bdf/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/512x512bb.png`;
 *   binance.com роботу отдаёт 202 без содержимого.
 * - `bybit` — favicon официальной документации API,
 *   `bybit-exchange.github.io/docs/img/favicon.ico`, слой 256 px. Иконка
 *   App Store — тот же знак на чёрном, а не на тёмно-синем фоне образца
 *   владельца; favicon bybit.com — другой знак (полосы).
 * - `okx` — apple-touch-icon okx.com,
 *   `www.okx.com/cdn/assets/imgs/253/59830BB78B18A776.png`, 200 px.
 * - `hyperliquid` — аватар организации `hyperliquid-dex` на GitHub,
 *   `avatars.githubusercontent.com/u/129421375`, 270 px, круглый. На сайтах
 *   биржи знак только без круга, на прозрачном фоне.
 * - `coinbase` — иконка приложения в App Store (id 886427730):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple211/v4/bb/7b/31/bb7b3124-301f-c94d-41c3-b3d6bc09ac17/AppIcon-0-0-1x_U007ephone-0-1-0-85-220.png/512x512bb.png`;
 *   apple-touch-icon coinbase.com — знак 2016 года.
 * - `kraken` — иконка приложения в App Store (id 1481947260):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple221/v4/56/22/f8/5622f829-ff3a-01d4-75f4-af98b7efc90e/AppIcon-0-0-1x_U007ephone-0-1-85-220.png/512x512bb.png`;
 *   apple-touch-icon kraken.com — тот же знак со своим скруглением углов.
 *
 * Восемь бирж этапа 9 (ТЗ 1.79, §92; DEN-52), загружены 15.10.2026.
 * Иконки App Store — версия `.png` того же адреса (поиск отдаёт `.jpg`):
 *
 * - `mexc` — App Store (id 1605393003):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple211/v4/3a/be/c5/3abec505-f9ac-b22f-b137-5378d546ffcb/AppIcon-Blue-0-0-1x_U007epad-0-11-0-0-85-220.png/512x512bb.png`.
 * - `gate` — App Store (id 1294998195):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple211/v4/89/46/cb/8946cbc1-fdbe-bb64-b0ce-609d9d417e6f/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/512x512bb.png`.
 * - `lighter` — apple-touch-icon веб-приложения биржи,
 *   `app.lighter.xyz/apple-touch-icon.png`, 180 px; приложения в App Store нет.
 * - `bitget` — App Store (id 1442778704):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple211/v4/a6/86/3c/a6863c4b-37c4-da20-c961-5d1cd6da6e69/AppIcon-0-1x_U007epad-0-1-0-85-220-0.png/512x512bb.png`.
 * - `kucoin` — App Store (id 1378956601, витрина Сингапура; в американской
 *   его нет):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple221/v4/e9/2b/38/e92b38a8-7795-1739-9875-32c5820e9275/AppIcon-0-0-1x_U007epad-0-11-0-85-220.png/512x512bb.png`.
 * - `bingx` — App Store (id 1500217666):
 *   `is1-ssl.mzstatic.com/image/thumb/Purple211/v4/e1/28/a9/e128a983-ede4-c633-4ff4-b2d5953f4452/AppIcon_bingbon-0-0-1x_U007emarketing-0-8-0-sRGB-0-85-220.png/512x512bb.png`.
 * - `htx` — apple-touch-icon htx.com, `www.htx.com/pwa/icon-pwa-180.png`,
 *   180 px; в App Store под именем HTX — чужое приложение (flatexDEGIRO).
 * - `aster` — apple-touch-icon asterdex.com,
 *   `static.asterdexfx.com/cloud-futures/static/images/aster/apple-touch-icon.png`,
 *   180 px; «Aster Dex App» в App Store выпущен не биржей.
 */
const EXCHANGE_LOGOS: Record<KnownExchange, string> = {
  binance: binanceLogo,
  bybit: bybitLogo,
  okx: okxLogo,
  hyperliquid: hyperliquidLogo,
  coinbase: coinbaseLogo,
  kraken: krakenLogo,
  mexc: mexcLogo,
  gate: gateLogo,
  lighter: lighterLogo,
  bitget: bitgetLogo,
  kucoin: kucoinLogo,
  bingx: bingxLogo,
  htx: htxLogo,
  aster: asterLogo,
};

export interface ExchangeLogoProps {
  exchange: string;
  className?: string;
  /**
   * Название биржи стоит рядом словом: знак пустой для диктора (`alt=""`, без
   * `title`), а у неизвестной биржи вместо имени — монограмма.
   */
  decorative?: boolean;
}

/**
 * Знак биржи с текстовой альтернативой: `<img alt>` для известной биржи,
 * имя словом для прочих (`web-density-cards`, «Биржа без логотипа»). Новая
 * биржа из §3.1 добавляет файл и строку в `EXCHANGE_NAMES`/`EXCHANGE_LOGOS`,
 * а до тех пор показывается словом.
 */
export function ExchangeLogo({ exchange, className, decorative = false }: ExchangeLogoProps) {
  if (!isKnownExchange(exchange)) {
    if (decorative) {
      return (
        <span
          aria-hidden="true"
          data-slot="exchange-logo-monogram"
          className={cn(
            "inline-flex shrink-0 items-center justify-center bg-secondary text-[8px] font-semibold text-muted-foreground",
            className,
          )}
        >
          {Array.from(exchangeLabel(exchange))[0]?.toUpperCase()}
        </span>
      );
    }
    return (
      <span
        data-slot="exchange-logo-fallback"
        className={cn("text-xs font-medium text-muted-foreground", className)}
      >
        {exchangeLabel(exchange)}
      </span>
    );
  }
  const name = EXCHANGE_NAMES[exchange];
  const logo = (
    <img
      data-slot="exchange-logo"
      src={EXCHANGE_LOGOS[exchange]}
      alt={decorative ? "" : name}
      draggable={false}
      className={cn(DEFAULT_LOGO_CLASS, className)}
    />
  );
  if (decorative) {
    return logo;
  }
  return (
    <Tooltip>
      <TooltipTrigger render={logo} />
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  );
}
